import React, { useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, Typography, Grid } from "@mui/material";
import { useDropzone } from "react-dropzone";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import axios from "axios";
import moment from "moment";

const AuthorizedSignatoryDialog = ({ open, onClose, signatory, onSave, customerId }) => {
  const [formData, setFormData] = useState({
    name: "",
    comment: "",
    idDocument: null,
  });

  const [idDocumentFile, setIdDocumentFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  React.useEffect(() => {
    if (signatory) {
      setFormData({
        name: signatory.name || "",
        comment: signatory.comment || "",
        idDocument: signatory.idDocument,
      });
    }
  }, [signatory]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleIdDocumentDrop = async (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setIdDocumentFile(file);
      try {
        const fileInfo = {
          fileName: file.name,
          fileSize: file.size,
          mimeType: file.type,
          creationDate: moment().format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z",
        };
        const fileInfoRes = await axios.post("/api/v1/file", fileInfo);
        const fileId = fileInfoRes.data.fileId;

        const formData = new FormData();
        formData.append("file", file, fileId);
        await axios.post("/api/v1/azureblobstorage", formData);

        setFormData((prev) => ({
          ...prev,
          idDocument: fileId,
        }));
      } catch (error) {
        console.error("Error uploading ID document:", error);
      }
    }
  };

  const { getRootProps: getIdDocumentRootProps, getInputProps: getIdDocumentInputProps } = useDropzone({
    onDrop: handleIdDocumentDrop,
    accept: {
      "application/pdf": [".pdf"],
    },
    maxFiles: 1,
  });

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const signatoryData = {
        ...formData,
        id: signatory?.id,
        entityId: customerId,
        isComplete: formData.name && formData.idDocument,
      };
      await onSave(signatoryData);
    } catch (error) {
      console.error("Error saving Authorized Signatory:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{signatory?.id ? "Edit Authorized Signatory" : "Add Authorized Signatory"}</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField name="name" label="Full Name" value={formData.name} onChange={handleChange} fullWidth required />
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="comment"
                label="Comment"
                value={formData.comment}
                onChange={handleChange}
                fullWidth
                multiline
                rows={3}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Authorized Signatory ID Document
              </Typography>
              <Box
                {...getIdDocumentRootProps()}
                sx={{
                  border: "2px dashed",
                  borderColor: "grey.300",
                  borderRadius: 1,
                  p: 2,
                  textAlign: "center",
                  cursor: "pointer",
                  bgcolor: formData.idDocument ? "success.light" : "background.paper",
                }}
              >
                <input {...getIdDocumentInputProps()} />
                <CloudUploadIcon sx={{ fontSize: 32, color: "primary.main", mb: 1 }} />
                <Typography>
                  {formData.idDocument ? "ID Document uploaded" : "Drop Authorized Signatory ID document here or click to select"}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  PDF only
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary" disabled={isSubmitting || !formData.name}>
          {isSubmitting ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AuthorizedSignatoryDialog;
